/**
 * Narrow a type of an object based on its prop type
 *
 * Imagine this:
 * ```
 * type Foo = FooX | FooY;
 * interface Props {
 *   foo: Foo;
 *   bar: Something;
 * }
 *
 * declare const isFooX: (f: Foo) => f is FooX;
 * declare const FooXDetail: React.FC<{foo: FooX; bar: Something}>;
 * ...
 * declare const props: Props;
 * if (isFooX(props.foo)) {
 *   // does not work, although it is obvious to human eyes, Typescript
 *   // does not see the narrowed type of props.foo here
 *   return <FooXDetail {...props} />;
 * }
 * ```
 *
 * So, isPropOfType comes to help:
 * ```
 * if (isPropOfType(props, 'foo', isFooX)) {
 *   // works like a charm now, since we explicitly told typescript that
 *   // props type is constrained with {foo: FooX}.
 *   return <FooXDetail {...props} />;
 * }
 * ```
 */
export const isPropOfType = <
  TObject,
  TProp extends keyof TObject,
  TNarrowedProp extends TObject[TProp],
>(
  object: TObject,
  prop: TProp,
  guard: (x: TObject[TProp]) => x is TNarrowedProp,
): object is TObject & { [Key in TProp]: TNarrowedProp } => guard(object[prop]);
